import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Menu } from '@mantine/core'
import {
  IconAlertTriangle, IconChevronDown, IconCircleCheck, IconCircleX, IconLoader2, IconSend, IconSquare,
} from '@tabler/icons-react'
import { GenerationForm } from '../generation/GenerationForm'
import { AdAgentProgress } from '../generation/AdAgentPopover'
import { displaySteps } from '../generation/generationSteps'
import { IconSparklesTrio, IconSparklesTwo } from '../icons'
import { cx } from '../../utils/cx'
import classes from './AssistantPanel.module.css'

export const CHAT_SUGGESTIONS = [
  { labelKey: 'editorV2.assistant.suggestions.premium.label', promptKey: 'editorV2.assistant.suggestions.premium.prompt' },
  { labelKey: 'editorV2.assistant.suggestions.shorterSlogan.label', promptKey: 'editorV2.assistant.suggestions.shorterSlogan.prompt' },
  { labelKey: 'editorV2.assistant.suggestions.anotherPhoto.label', promptKey: 'editorV2.assistant.suggestions.anotherPhoto.prompt' },
]

function Steps({ job, done = false }) {
  const { t } = useTranslation()
  const steps = done ? displaySteps({ status: 'completed' }) : displaySteps(job)
  return (
    <ul className={classes.steps}>
      {steps.map((step) => (
        <li key={step.key} className={classes.step} data-state={step.state}>
          {step.state === 'done' && <IconCircleCheck size={16} stroke={1.8} className={classes.done} aria-label={t('editorV2.assistant.steps.done')} />}
          {step.state === 'failed' && <IconCircleX size={16} stroke={1.8} className={classes.failed} aria-label={t('editorV2.assistant.steps.failed')} />}
          {['active', 'pending'].includes(step.state) && (
            <IconLoader2 size={16} stroke={1.8} className={cx(classes.pending, step.state === 'active' && classes.spin)} aria-label={step.state === 'active' ? t('editorV2.assistant.steps.inProgress') : t('editorV2.assistant.steps.waiting')} />
          )}
          <span>{step.label}</span>
        </li>
      ))}
    </ul>
  )
}

function SuccessAlert({ review }) {
  const { t } = useTranslation()
  return (
    <div className={classes.success} data-tone={review ? 'review' : undefined} role="status">
      {review
        ? <IconAlertTriangle size={28} stroke={1.5} className={classes.reviewIcon} />
        : <IconCircleCheck size={28} stroke={1.5} className={classes.done} />}
      <div className={classes.successText}>
        <span className={classes.successTitle}>{review ? t('editorV2.assistant.success.reviewTitle') : t('editorV2.assistant.success.title')}</span>
        <span className={classes.successMessage}>
          {review
            ? t('editorV2.assistant.success.reviewMessage')
            : t('editorV2.assistant.success.message')}
        </span>
      </div>
    </div>
  )
}

function Reply({ message, undoable, onUndo, pending, onApply, onDiscard }) {
  const { t } = useTranslation()
  return (
    <div className={classes.replyRow}>
      <div className={classes.reply}>
        {message.content && <p className={classes.replyText}>{message.content}</p>}
        {message.changes?.length > 0 && (
          <ul className={classes.changes}>
            {message.changes.map((change) => <li key={change}>{change}</li>)}
          </ul>
        )}
        {message.followUp && <p className={classes.replyText}>{message.followUp}</p>}
        {message.warnings?.map((warning) => <p key={warning} className={classes.warning}>{warning}</p>)}
        {pending && (
          <div className={classes.pendingBlock}>
            <p className={classes.replyText}>{t('editorV2.assistant.reply.pendingQuestion')}</p>
            <div className={classes.pendingActions}>
              <button type="button" className={classes.smallPrimary} onClick={onApply}>{t('editorV2.assistant.reply.apply')}</button>
              <button type="button" className={classes.smallButton} onClick={onDiscard}>{t('editorV2.assistant.reply.discard')}</button>
            </div>
          </div>
        )}
        {undoable && (
          <button type="button" className={classes.undo} onClick={onUndo}>{t('editorV2.assistant.reply.undo')}</button>
        )}
      </div>
    </div>
  )
}

/**
 * Smartico AI Assistant (Figma 492:20380 in the Chat tab, 226:18740 as the narrow panel):
 * 61px header, a thread centred in a 540px column, and the composer — 80px message field,
 * model menu and Send / Stop. The welcome state opens the generation form ("Generate Ad with
 * AI"); while it is open, Send starts the generation.
 */
export function AssistantPanel({
  assistant,
  job,
  generating,
  form,
  apiBase,
  language,
  onGenerate,
  onCancelGeneration,
  formOpen,
  setFormOpen,
  generationError,
  agentLabel,
  agentPipeline,
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')
  const scroller = useRef(null)
  const running = Boolean(job) && !['completed', 'failed'].includes(job.status)
  const busy = assistant.status === 'working' || running || generating
  const empty = !assistant.messages.length && !formOpen && !running

  useEffect(() => {
    const node = scroller.current
    if (node) node.scrollTop = node.scrollHeight
  }, [assistant.messages.length, assistant.working, running, job?.currentStep, formOpen, assistant.pending])

  const submit = () => {
    if (busy) return
    if (formOpen) {
      form.setAttempted(true)
      if (!form.advertiser) return
      if (draft.trim() && !form.briefing.trim()) form.setBriefing(draft.trim())
      setDraft('')
      onGenerate({ briefingOverride: draft.trim() && !form.briefing.trim() ? draft.trim() : null })
      return
    }
    const text = draft.trim()
    if (!text) return
    setDraft('')
    assistant.send(text)
  }

  const stop = () => {
    if (running || generating) onCancelGeneration()
    else assistant.stop()
  }

  const currentModel = assistant.models.find((entry) => entry.id === assistant.model) || assistant.models[0]

  return (
    <section className={classes.panel} aria-label={t('editorV2.assistant.title')}>
      <header className={classes.header}>
        <h2 className={classes.title}>{t('editorV2.assistant.title')}</h2>
      </header>

      <div ref={scroller} className={classes.main}>
        <div className={classes.column}>
          {empty && (
            <div className={classes.welcome}>
              <IconSparklesTrio size={42} className={classes.welcomeIcon} />
              <div>
                <p className={classes.welcomeTitle}>{t('editorV2.assistant.welcome.title')}</p>
                <p className={classes.welcomeText}>
                  {t('editorV2.assistant.welcome.text')}
                </p>
              </div>
              <button type="button" className={classes.primary} onClick={() => setFormOpen(true)}>
                {t('editorV2.assistant.welcome.generate')}
                <IconSend size={16} stroke={1.8} />
              </button>
            </div>
          )}

          {assistant.messages.map((message) => {
            if (message.role === 'user') {
              return (
                <div key={message.id} className={classes.userRow}>
                  <p className={classes.user}>{message.content}</p>
                </div>
              )
            }
            if (message.role === 'generation') {
              return message.status === 'completed' ? (
                <div key={message.id} className={classes.generation}>
                  <Steps done />
                  <SuccessAlert review={message.quality === 'draft'} />
                </div>
              ) : (
                <p key={message.id} className={classes.error}>{message.content || t('editorV2.assistant.generationFailed')}</p>
              )
            }
            if (message.role === 'error') return <p key={message.id} className={classes.error}>{message.content}</p>
            return (
              <Reply
                key={message.id}
                message={message}
                undoable={assistant.undoableId === message.id}
                onUndo={assistant.undo}
                pending={assistant.pending?.messageId === message.id}
                onApply={assistant.applyPending}
                onDiscard={assistant.discardPending}
              />
            )
          })}

          {formOpen && !running && (
            <div className={classes.form}>
              <GenerationForm
                form={form}
                variant="chat"
                apiBase={apiBase}
                language={language}
                disabled={busy}
                error={generationError}
                narrow
              />
            </div>
          )}

          {running && (
            <div className={classes.generation} role="status" aria-live="polite" aria-busy="true">
              <Steps job={job} />
              <div className={classes.agent}>
                <AdAgentProgress job={job} agentLabel={agentLabel} agentPipeline={agentPipeline} />
              </div>
            </div>
          )}

          {assistant.showSuggestions && !formOpen && (
            <div className={classes.suggestions}>
              <span className={classes.suggestionsLabel}>{t('editorV2.assistant.suggestionsLabel')}</span>
              <div className={classes.chips}>
                {CHAT_SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion.labelKey}
                    type="button"
                    className={classes.smallButton}
                    disabled={busy || !assistant.enabled}
                    onClick={() => assistant.send(t(suggestion.promptKey))}
                  >
                    <IconSparklesTwo size={14} stroke={1.8} />
                    {t(suggestion.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {assistant.working && (
            <div className={classes.working} role="status" aria-live="polite">
              <IconLoader2 size={16} stroke={1.8} className={cx(classes.pending, classes.spin)} />
              <span>{assistant.working}</span>
            </div>
          )}
        </div>
      </div>

      <footer className={classes.footer}>
        <div className={classes.column}>
          <textarea
            className={classes.composer}
            placeholder={t('editorV2.assistant.composer.placeholder')}
            aria-label={t('editorV2.assistant.composer.label')}
            value={draft}
            maxLength={2000}
            disabled={!assistant.enabled && !formOpen}
            onChange={(event) => setDraft(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                event.preventDefault()
                submit()
              }
            }}
          />
          <div className={classes.buttons}>
            <Menu position="top-start" shadow="md" withinPortal disabled={assistant.models.length < 2}>
              <Menu.Target>
                <button type="button" className={classes.model} aria-label={t('editorV2.assistant.composer.model')}>
                  {currentModel?.label || t('editorV2.assistant.composer.defaultModel')}
                  <IconChevronDown size={16} stroke={1.8} />
                </button>
              </Menu.Target>
              <Menu.Dropdown>
                {assistant.models.map((entry) => (
                  <Menu.Item key={entry.id} onClick={() => assistant.setModel(entry.id)} fw={entry.id === assistant.model ? 600 : 400}>
                    {entry.label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
            {busy ? (
              <button type="button" className={classes.primary} data-size="lg" onClick={stop}>
                {t('editorV2.assistant.composer.stop')}
                <IconSquare size={20} stroke={1.8} />
              </button>
            ) : (
              <button
                type="button"
                className={classes.primary}
                data-size="lg"
                disabled={formOpen ? false : (!assistant.enabled || !draft.trim())}
                onClick={submit}
              >
                {t('editorV2.assistant.composer.send')}
                <IconSend size={20} stroke={1.8} />
              </button>
            )}
          </div>
          {!assistant.enabled && !formOpen && (
            <p className={classes.disabledNote}>{t('editorV2.assistant.composer.disabled')}</p>
          )}
        </div>
      </footer>
    </section>
  )
}
